import { confirmation, notify, RichTextValue } from '@/components';
import {
  useAcceptTermsAndConditionMutation,
  useUserContextSelector,
} from '@/redux';
import { useGetPendingTermsAndConditionsQuery } from '@/redux/termsAndConditions';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';

const useAccept = () => {
  const [accept] = useAcceptTermsAndConditionMutation();
  const [accepted, setAccepted] = useState(false);

  const handleAccept = async (id: string, termsId: string) => {
    await accept({ id, termsId })
      .unwrap()
      .then(() => {
        notify.success.t('termsAndConditions.accept.saved');
        setAccepted(true);
      });
  };

  return {
    accept: handleAccept,
    accepted,
    setAccepted,
  };
};

export function TermsGuard() {
  const id = useUserContextSelector((x) => x.id);
  const { data, isFetching } = useGetPendingTermsAndConditionsQuery({});
  const { version: pendingTermsVersion } = data ?? {};
  const { t } = useTranslation();

  const { accept, accepted, setAccepted } = useAccept();

  const shownRef = useRef(false);

  useEffect(() => setAccepted(false), [setAccepted, pendingTermsVersion]);

  useEffect(() => {
    const isAccepted = accepted;
    if (isAccepted || shownRef.current || isFetching || !data) {
      return;
    }

    shownRef.current = true;

    confirmation.show({
      title: t('termsAndConditions.accept.title'),
      confirm: t('termsAndConditions.accept.confirm'),
      hideCancelButton: true,
      maskClosable: false,
      body: (
        <RichTextValue
          ownerType="TermsAndConditions"
          value={data.content}
          label={false}
          ownerId=""
        />
      ),
      onConfirm: async () => {
        await accept(id, data.id);
        shownRef.current = false;
      },
    });
  }, [data, accept, id, isFetching, t, pendingTermsVersion, accepted]);

  return <Outlet />;
}
